import { getCleanPageData } from '@/utils/tracking';
import { useRouter } from 'next/router';
import { createContext, useContext, ReactNode, useEffect, useRef, useCallback } from 'react';
import { getBooleanParam } from '@/utils/urlParams';

interface TrackingContextType {
  trackSearch: (searchTerm: string, isAutoComplete: boolean) => void;
  trackRouteChange: (pageCategory?: string) => void;
}

const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

interface TrackingProviderProps {
  children: ReactNode;
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  const router = useRouter();
  const isFirstChange = useRef(true);
  const lastPage = useRef<string | null>(null);
  const pauseMilestonesUntil = useRef<number>(0);
  const reachedMilestones = useRef<Set<number>>(new Set());
  const scrollMilestones = useRef([25, 50, 75, 90, 100]);
  const debugTrackingRef = useRef(getBooleanParam('debugTracking'));
  const emitPageReady = useRef(getBooleanParam('emitPageReady'));


  const trackEvent = useCallback((event: string, params: Record<string, any>) => {
    window.dataLayer?.push({ event, ...params });
    if (debugTrackingRef.current) {
      console.group(`Tracking Event: ${event || 'parameters only'}`);
      console.log('Params:', params);
      console.groupEnd();
    }
  }, [debugTrackingRef.current]);

  const trackSearch = (searchTerm: string, isAutoComplete: boolean) => {
    // Ensure the search term is clean (e.g., lowercase, no leading/trailing spaces)
    const cleanedSearchTerm = searchTerm.trim().toLowerCase();
    trackEvent('site_search_submit', {
      search_term: cleanedSearchTerm,
      autocomplete_used: isAutoComplete ? 'true' : 'false', // GA4 best practice is to send strings
    });
  }

  const resetScrollTracking = useCallback(() => {
    pauseMilestonesUntil.current = Date.now() + 1000; // Pause for 1 second
    reachedMilestones.current.clear();
    if (debugTrackingRef.current) {
      console.group("scroll_depth url on reset");
      console.log("Route changed to:", router.asPath);
      console.groupEnd();
    }
    trackEvent('scroll_depth', { scroll_percentage: '0%' });
  }, [router]);

  const trackRouteChange = useCallback((pageCategory?: string) => {
    const { pageLocationClean, pageTitleClean } = getCleanPageData(router);
    if (emitPageReady.current) {
      const pageReadyEvent = new CustomEvent('page_ready');
      window.dispatchEvent(pageReadyEvent);
    }
    if (isFirstChange.current) {
      isFirstChange.current = false;
      const vars: Record<string, any> = { pageLocationClean, pageTitleClean, pageCategory: pageCategory || 'uncategorized' };
      if (lastPage.current) vars['pageReferrer'] = lastPage.current;
      trackEvent('', vars);
      resetScrollTracking();
      lastPage.current = pageLocationClean;
      return;
    }
    if (debugTrackingRef.current) {
      console.group("manual_page_view parameters");
      console.log("PageReferrer:", lastPage.current);
      console.log("PageLocation:", pageLocationClean);
      console.log("PageTitle:", pageTitleClean);
      console.log("PageCategory:", pageCategory || 'uncategorized');
      console.groupEnd();
    }
    trackEvent('manual_page_view', {
      pageReferrer: lastPage.current,
      pageLocationClean: pageLocationClean,
      pageTitleClean: pageTitleClean,
      pageCategory: pageCategory || 'uncategorized',
    });
    resetScrollTracking();
    lastPage.current = pageLocationClean;
  }, [router]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = Math.max(0, window.scrollY);
      if (Date.now() >= pauseMilestonesUntil.current && router.isReady) {
        const windowHeight = window.innerHeight;
        const docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        const scrollPercent = ((scrollY + windowHeight) / (docHeight - 1)) * 100;
        for (const milestone of scrollMilestones.current) {
          if (scrollPercent >= milestone && !reachedMilestones.current.has(milestone)) {
            reachedMilestones.current.add(milestone);
            if (debugTrackingRef.current) {
              console.group(`scroll_depth milestone ${milestone}%`);
              console.log('Scroll Percent:', scrollPercent);
              console.groupEnd();
            }
            trackEvent('scroll_depth', { scroll_percentage: `${milestone}%` });
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [router.isReady]);

  return (
    <TrackingContext.Provider value={{ trackSearch, trackRouteChange }}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = (): TrackingContextType => {
  const context = useContext(TrackingContext);
  return context || {
    trackSearch: () => {},
    trackRouteChange: () => {}
  };
};


export const useTrackRouteChange = (category: string) => {
  const router = useRouter();
  const { trackRouteChange } = useTracking();
  useEffect(() => {
    if (router.isReady) trackRouteChange(category);
  }, [router.isReady]);
}